import { useEffect, useState } from "react";

import { Button, Grid, Paper, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  DeleteBanner,
  UpdatePriority,
  getAllBanners,
  getById,
} from "../../Api/BannerDetails/Banner.Api";
import { ReactComponent as Banner } from "../../Assets/Images/BannerIcon.svg";
import { NoRecordsFound } from "../../Components/atoms/NoRecordsFound/NoRecordsFound";
import {
  Heading1,
  Heading3,
} from "../../Components/atoms/Typography/Heading/Heading";
import { AddBanner } from "../../Components/organisms/AddBanner/AddBanner";
import { BasicGrid } from "../../Components/organisms/BasicGrid/BasicGrid";
import ConfirmDialog from "../../Components/organisms/ConfirmDialog/ConfirmDialog";
import useLoadingState from "../../Utils/Hooks/useLodingState";
import useOverlayState from "../../Utils/Hooks/useOverlayState";
import { getColumns } from "./GridColumns";

export const BannersGrid = () => {
  const { OverlayState, openOverlay, closeOverlay } = useOverlayState();
  const { LoadingState, startLoading, stopLoading } = useLoadingState();
  const [bannersData, setBannersData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getAllBanners();

      if (response && response.type === 0 && response.data) {
        const sortedData = response.data.sort(
          (a, b) => (a.priority || 0) - (b.priority || 0),
        );
        const processedData = sortedData.map((banner, index) => ({
          ...banner,
          priority: banner.priority || index + 1,
          attributeValue:
            typeof banner.attributeValue === "string"
              ? JSON.parse(banner.attributeValue)
              : banner.attributeValue,
        }));

        setBannersData(processedData);
      } else {
        setError(response?.message || "Failed to fetch banner data");
        setBannersData([]);
      }
    } catch (error) {
      setError(error.message || "Network error occurred");
      setBannersData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePriorityChange = async (bannerId, newPriority, bannerName) => {
    const currentBanner = bannersData.find(
      (b) => b.generalRecordId === bannerId,
    );
    const targetBanner = bannersData.find((b) => b.priority === newPriority);

    if (!currentBanner) {
      return;
    }

    const updatedPriorityData = [];

    updatedPriorityData.push({
      BannerId: currentBanner.generalRecordId,
      Priority: newPriority,
    });

    if (targetBanner) {
      updatedPriorityData.push({
        BannerId: targetBanner.generalRecordId,
        Priority: currentBanner.priority,
      });
    }

    //api call
    startLoading({ btnName: "priorityChange" });
    await UpdatePriority(updatedPriorityData);
    stopLoading();

    const updatedData = bannersData.map((banner) => {
      if (banner.generalRecordId === currentBanner.generalRecordId) {
        return { ...banner, priority: newPriority };
      }
      if (
        targetBanner &&
        banner.generalRecordId === targetBanner.generalRecordId
      ) {
        return { ...banner, priority: currentBanner.priority };
      }
      return banner;
    });

    const sortedData = updatedData.sort((a, b) => a.priority - b.priority);
    setBannersData(sortedData);
  };

  const clickHandler = async (e, data) => {
    startLoading({ btnName: "load" });

    if (e === 1) {
     const bannerResponse = await getById(data.generalRecordId);
      console.log(bannerResponse);
      if (bannerResponse && bannerResponse.type === 0 && bannerResponse.data) {
        openOverlay({
          mode: "view",
          OverlayName: "new-banner",
          record: bannerResponse.data, 
        });
      } else {
        console.error("Failed to fetch banner details:", bannerResponse?.message);
      }
    } else if (e === 2) {
      //for future use
      openOverlay({
        mode: "update",
        OverlayName: "new-banner",
        record: data,
      });
    } else if (e === 3) {
      openOverlay({
        OverlayName: "delete-banner",
        record: data,
        messages: {
          title: "Delete",
          description:
            "Are you sure you want to delete this banner?",
        },
      });
    }
    stopLoading();
  };

  const handleRefetch = () => {
    fetchBanners();
  };

  return (
    <>
      <Grid container direction={"column"}>
        <Grid item>
          <Stack direction={"row"} justifyContent={"space-between"} mb={2.8}>
            <Heading1 text={"Banners"} />
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <Button
                disableTouchRipple
                variant="lightBlue"
                sx={{ borderRadius: "14px" }}
                onClick={() => {
                  openOverlay({ OverlayName: "new-banner", mode: "create" });
                }}
              >
                <Banner style={{ marginRight: "10px" }} fill="#ffffff" />
                <Heading3 text={"New Banner"} color="#ffffff" />
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid width="100%" item>
          <Paper
            sx={{
              padding: "20px 24px",
              boxShadow: "0px 0px 25px 0px rgba(0, 0, 0, 0.05)",
              position: "relative",
              height: "100%",
            }}
          >
            <BasicGrid
              isLoading={isLoading || LoadingState.visible}
              data={bannersData}
              columnArray={getColumns(
                t,
                clickHandler,
                handlePriorityChange,
                bannersData.length,
              )}
              allowSorting={false}
              allowPaging={false}
              field={["generalRecordId", "attributeValue"]}
            />
          </Paper>
        </Grid>
      </Grid>

      {OverlayState.visible && OverlayState.OverlayName === "new-banner" && (
        <AddBanner
          close={closeOverlay}
          OpenDrawer={OverlayState.visible}
          mode={OverlayState.mode}
          data={OverlayState.record}
          refetch={handleRefetch}
        />
      )}

      {OverlayState.visible && OverlayState.OverlayName === "delete-banner" && (
        <ConfirmDialog
          title={OverlayState.messages?.title}
          description={OverlayState.messages?.description}
          open={OverlayState?.visible}
          close={closeOverlay}
          onNoClick={closeOverlay}
          onOkTitle={"Yes"}
          onOkClick={async () => {
            const id = OverlayState.record?.generalRecordId;

            await DeleteBanner(id);
            closeOverlay();
            handleRefetch();
          }}
        />
      )}

      {OverlayState.visible &&
        !OverlayState.OverlayName &&
        OverlayState?.messages?.title && (
          <ConfirmDialog
            isError={OverlayState.mode === "fail" || OverlayState.mode === 3}
            title={OverlayState.messages?.title}
            description={OverlayState.messages?.description}
            open={OverlayState?.visible}
            close={closeOverlay}
            onOkClick={closeOverlay}
          />
        )}
    </>
  );
};